export type OrderCartV2Addon = {
  subItemId: string;
  addonGroupId: string;
  quantity: number;
  unitPrice: number;
  unitPriceBeforeDiscount?: number;
  customTaxPercentage?: number;
  nestedAddons?: OrderCartV2Addon[];
  /** Client-only display metadata — stripped before calculate API calls. */
  displayName?: string;
  /**
   * Client-only: the AddonGroupSubItem ROW id of the selected option — the id
   * POST /receipt/create expects in addons[].id. Persisted at add-to-cart time
   * so display-tree rebuilds never have to re-derive it from menu-lookup
   * templates (which keep only the last-added line's options per sub-item and
   * previously made rebuilds leak the addon's sub-item id instead). Stripped
   * before calculate API calls.
   */
  addonGroupSubItemId?: number;
};

export type OrderCartV2Line = {
  lineId?: string;
  itemId: string;
  subItemId: string;
  quantity: number;
  unitPrice: number;
  unitPriceBeforeDiscount?: number;
  note?: string;
  customTaxPercentage?: number;
  addons?: OrderCartV2Addon[];
  appliedOffer?: OrderCartV2LineAppliedOffer;
};

export type OrderLineType = OrderCartV2Line;

export type OrderCartV2OfferSelection = {
  lineId: string;
  quantity: number;
};

export type OrderCartV2AppliedOffer = {
  offerApplicationId: string;
  offerId: string;
  selectionMode?: "AUTO" | "EXPLICIT";
  selections?: OrderCartV2OfferSelection[];
  triggers?: OrderCartV2OfferSelection[];
};

type OrderCartV2Rounding = {
  method: string;
  precision?: number;
};

type OrderCartV2PayWithPointsInput = {
  useMaxPoints?: boolean;
  points?: number;
};

export type OrderCartV2Input = {
  cartVersion: number;
  config: {
    fees: {
      taxPercentage: number;
      serviceChargePercentage?: number;
      deliveryFees?: number;
    };
    pricing?: {
      discountCalculationPhase?: "BEFORE_TAX" | "AFTER_TAX";
      taxCalculationBase?: "SUBTOTAL" | "SUBTOTAL_PLUS_SERVICE";
      rounding?: OrderCartV2Rounding;
    };
    behavior?: {
      offerApplicationMode: "SPLIT" | "INLINE";
      mergeIdenticalLines?: boolean;
      previewUnappliedExplicitOffers?: boolean;
    };
    payWithPoints?: OrderCartV2PayWithPointsInput;
  };
  lines: OrderCartV2Line[];
  appliedOffers?: OrderCartV2AppliedOffer[];
};

// Response types

type OrderCartV2PricingBreakdown = {
  item: number;
  addons: number;
  total: number;
};

type OrderCartV2LineUnitPricing = {
  subtotal: OrderCartV2PricingBreakdown;
  discount?: OrderCartV2PricingBreakdown;
  externalDiscount?: OrderCartV2PricingBreakdown;
  total: OrderCartV2PricingBreakdown;
};

type OrderCartV2LinePricing = {
  unit: OrderCartV2LineUnitPricing;
  subtotal: number;
  discount?: number;
  externalDiscount?: number;
  tax: number;
  serviceCharge?: number;
  total: number;
};

export type OrderCartV2LineAppliedOffer = {
  offerApplicationId: string;
  quantity: number;
};

export type OrderCartV2ResponseLine = {
  lineId: string;
  itemId: string;
  subItemId: string;
  quantity: number;
  unitPrice: number;
  note?: string;
  pricing: OrderCartV2LinePricing;
  appliedOffer?: OrderCartV2LineAppliedOffer;
  excludedFromTotals?: boolean;
  /** From menu item metadata on calculate — maps to cart `excludeFromOffers`. */
  isExcludedFromOffers?: boolean;
  /** From menu item metadata on calculate — maps to cart `excludeFromPointsPayment`. */
  isExcludedFromPointsPayment?: boolean;
};

type OrderCartV2Discounts = {
  offers: number;
  external?: number;
  total: number;
};

type OrderCartV2DeliveryPricing = {
  subtotal: number;
  discount: number;
  total: number;
};

export type OrderCartV2PayWithPointsAmount = {
  points: number;
  amount: number;
};

type OrderCartV2PayWithPointsResult = {
  applied?: OrderCartV2PayWithPointsAmount;
  maximum?: OrderCartV2PayWithPointsAmount;
};

type OrderCartV2Pricing = {
  subtotal: number;
  discounts: OrderCartV2Discounts;
  serviceCharge?: number;
  tax: number;
  delivery: OrderCartV2DeliveryPricing;
  totalBeforePayWithPoints?: number;
  payWithPoints?: OrderCartV2PayWithPointsResult;
  total: number;
};

type OrderCartV2PointsEarned = {
  base: number;
  bonus: number;
  total: number;
};

type OrderCartV2PointsRedeemed = {
  payWithPoints?: number;
  milestoneOffers?: number;
  total: number;
};

export type OrderCartV2Points = {
  balance: number;
  earned: OrderCartV2PointsEarned;
  redeemed?: OrderCartV2PointsRedeemed;
};

type OrderCartV2AppliedOfferReasonDetails = {
  minimumSpendAmount?: number;
  currentDiscountableSubtotal?: number;
  requiredAdditional?: number;
};

type OrderCartV2AppliedOfferReason = {
  code: string;
  message: string;
  details?: OrderCartV2AppliedOfferReasonDetails;
};

export enum LoyaltyOfferStatus {
  APPLIED = "APPLIED",
  NOT_APPLIED = "NOT_APPLIED",
  INVALID = "INVALID",
}

type OrderCartV2AppliedOfferResponse = {
  offerApplicationId: string;
  offerId: string;
  selectionMode?: string;
  selectionCriteria?: string;
  selections?: OrderCartV2OfferSelection[];
  triggers?: OrderCartV2OfferSelection[];
  status: LoyaltyOfferStatus;
  reason?: OrderCartV2AppliedOfferReason;
  aggregatePricing?: { subtotal: number; discount: number; total: number };
};

export type OrderCartV2Output = {
  lines: OrderCartV2ResponseLine[];
  pricing: OrderCartV2Pricing;
  points: OrderCartV2Points;
  appliedOffer?: OrderCartV2AppliedOfferResponse;
};
